import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const shuffle = (arr) => arr.map((t) => [Math.random(), t]).sort((a, b) => a[0] - b[0]).map((x) => x[1]);

// Encoder-only music bed: plays the admin playlist on loop; fades out while a Broadcast Cut-In is on air.
export function MusicBed({ muted, ducked }) {
  const [cfg, setCfg] = useState(null);
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const [trackTitle, setTrackTitle] = useState("");

  useEffect(() => {
    const load = () => api.get("/music").then((r) => setCfg(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 30 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || !cfg?.enabled || !cfg.tracks?.length) return undefined;
    let retry = null;
    const next = () => {
      if (!queueRef.current.length) queueRef.current = shuffle(cfg.tracks);
      const track = queueRef.current.shift();
      setTrackTitle(track.title);
      el.src = track.url;
      el.play().catch(() => { retry = setTimeout(() => el.play().catch(() => {}), 3000); });
    };
    el.addEventListener("ended", next);
    el.addEventListener("error", next);
    next();
    return () => { clearTimeout(retry); el.removeEventListener("ended", next); el.removeEventListener("error", next); el.pause(); };
  }, [cfg]);

  // Smooth 1s fade between the bed level, the ducked level (voice speaking) and silence (cut-in)
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !cfg) return undefined;
    const base = Math.max(0, Math.min(1, (cfg.volume ?? 35) / 100));
    const target = muted ? 0 : ducked ? base * ((cfg.duck_music_pct ?? 20) / 100) : base;
    const step = () => {
      const diff = target - el.volume;
      if (Math.abs(diff) < 0.02) { el.volume = target; return; }
      el.volume = Math.max(0, Math.min(1, el.volume + Math.sign(diff) * 0.04));
      raf = setTimeout(step, 40);
    };
    let raf = setTimeout(step, 0);
    return () => clearTimeout(raf);
  }, [muted, ducked, cfg]);

  if (!cfg?.enabled) return null;
  return <audio ref={audioRef} data-testid="music-bed" data-track={trackTitle} data-muted={muted ? "1" : "0"} data-ducked={ducked ? "1" : "0"} preload="auto" />;
}
